import { Attachment } from '@app-store'
import React, { useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import { COMM_TEMPLATE, MODAL_TYPES } from '../../utils/constants'
import {
  getFileIcon,
  SvgCross,
  SvgDocuSign,
  SvgDrag,
  SvgEdit,
  Tooltip,
} from '@ui-kit'
import { dataResponse } from './response'
import MiscUploadButton from './MiscUploadButtonAgent'
import { ShortcutKeys } from '../../constants/form.constants'
export interface DraggableAttachableListProps {
  setAttachmentsKey: React.Dispatch<
    React.SetStateAction<Attachment[] | undefined>
  >
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
  attachments: Attachment[]
  handleViewClick: (filename: string, path?: string) => void
  handleAttachmentEditClick: (item: Attachment, type: string) => void
  handleAttachmentDeleteClick: (item: Attachment) => void
  tabIndexOffset?: number
  hideUploadButton?: boolean
  label?: string
  isViewMode?: boolean
}
interface DraggableSectionPropsType {
  item: Attachment
  index: number
  tabIndexOffset?: number
  handleChange: (item: Attachment) => void
  handleAttachmentEditClick: (item: Attachment, type: string) => void
  handleAttachmentDeleteClick: (item: Attachment) => void
  isViewMode?: boolean
}
const DraggableSection = ({
  item,
  index,
  tabIndexOffset,
  handleAttachmentDeleteClick,
  handleAttachmentEditClick,
  isViewMode,
}: DraggableSectionPropsType) => {
  return (
    <Draggable draggableId={`${item?.id},${index}`} index={index}>
      {(provided) => (
        <li
          className="flex items-center"
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
          }}
        >
          <div
            {...provided.dragHandleProps}
            className="cursor-grab p-2 mr-1"
            tabIndex={tabIndexOffset}
          >
            {!isViewMode && <SvgDrag />}
          </div>
          <div className="data flex items-center border rounded-[0.25rem] p-2 border-border-200 h-10 w-[16rem]">
            <div className="flex justify-between items-center w-full">
              <Tooltip content={item?.attachment_name} position="bottom">
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAttachmentEditClick(item, MODAL_TYPES.LINK)
                  }}
                  className="flex items-center gap-2 justify-center"
                  tabIndex={tabIndexOffset ?? 0}
                  onKeyDown={(e) => {
                    if (
                      e.key === ShortcutKeys.Enter ||
                      e.key === ShortcutKeys.Space
                    ) {
                      e?.preventDefault()
                      handleAttachmentEditClick(item, MODAL_TYPES.LINK)
                    }
                  }}
                >
                  <div>
                    {getFileIcon(
                      item?.attachment_name ?? item?.attachment_type
                    )}
                  </div>
                  <span className="font-primary text-xs truncate">
                    <div className="ml-1 font-primary text-xs font-light text-ellipsis overflow-hidden cursor-pointer truncate w-[8rem]">
                      {item.attachment_name}
                    </div>
                  </span>
                </div>
              </Tooltip>
              <div className="flex items-center">
                {item?.contains_docusign && (
                  <div className="p-0.5 ml-auto docusign">
                    <div className="border-secondary-500">
                      <SvgDocuSign className="hover:border-secondary-500" />
                    </div>
                  </div>
                )}
                {item?.contains_docusign && item?.include_editable_text && (
                  <div className="text-accent-200">|&nbsp;&nbsp;</div>
                )}
                {item?.include_editable_text && (
                  <div
                    onClick={() =>
                      handleAttachmentEditClick(item, MODAL_TYPES.EDIT)
                    }
                    className="hover:bg-secondary-25 p-0.5 ml-auto edit"
                    role="button"
                  >
                    <div className="border-secondary-500">
                      <SvgEdit className="hover:border-secondary-500" />{' '}
                    </div>
                  </div>
                )}
                {item?.contains_docusign && item?.include_editable_text && (
                  <div className="text-accent-200">|&nbsp;&nbsp;</div>
                )}
                <button
                  onClick={() => handleAttachmentDeleteClick(item)}
                  type="button"
                  className="hover:bg-secondary-25 p-0.5 ml-auto edit"
                  tabIndex={tabIndexOffset}
                  onKeyDown={(e) => {
                    if (
                      e.key === ShortcutKeys.Enter ||
                      e.key === ShortcutKeys.Space
                    ) {
                      e?.preventDefault()
                      handleAttachmentDeleteClick(item)
                    }
                  }}
                >
                  {!isViewMode && (
                    <div className="border-secondary-500">
                      <SvgCross className="hover:border-secondary-500" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </li>
      )}
    </Draggable>
  )
}
const DraggableAttachableList = ({
  label = COMM_TEMPLATE.ATTACHMENTS,
  setAttachmentsKey,
  setAttachments,
  attachments,
  handleViewClick,
  handleAttachmentEditClick,
  handleAttachmentDeleteClick,
  tabIndexOffset,
  hideUploadButton,
  isViewMode,
}: DraggableAttachableListProps) => {
  const [render, setRender] = useState<number>(0)
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const reorderedAttachments = Array.from(attachments)
    const [removed] = reorderedAttachments.splice(result.source.index, 1)
    reorderedAttachments.splice(result.destination.index, 0, removed)
    setAttachments(reorderedAttachments || dataResponse)
    const tempAttachmentKey = reorderedAttachments.map((item: Attachment) => {
      return {
        id: item?.id,
        attachment_name: item?.attachment_name,
        attachment_document_type: item?.attachment_document_type,
        attachment_type: item?.attachment_type,
        attachment_document_id: item?.attachment_document_id,
        include_editable_text: item?.include_editable_text ?? false,
        contains_docusign: item?.contains_docusign,
        awsFileName: item?.awsFileName ?? '',
        ...(item?.path ? { path: item?.path } : {}),
      }
    })
    setAttachmentsKey(tempAttachmentKey)
  }
  const handleChange = (item: Attachment) => {
    if (
      item?.attachment_document_type !== COMM_TEMPLATE.STATIC_DOCUMENT_UPPERCASE
    )
      return
    handleViewClick(item?.attachment_url ?? '', item?.path?.toString() ?? '')
  }

  useEffect(() => {
    setRender(render + 1)
  }, [attachments])

  return (
    <div className="attachments custom-shadow mt-3 rounded-[0.25rem] flex-col p-3">
      <div className="flex justify-between mb-2">
        <strong className="text-base">{label}</strong>
        {!hideUploadButton ? (
          <MiscUploadButton
            setAttachments={setAttachments}
            setAttachmentsKey={setAttachmentsKey}
            tabIndex={tabIndexOffset}
          />
        ) : null}
      </div>
      <div className="w-full overflow-x-auto h-[7.9rem] p-3 flex flex-col">
        <span className="text-xs font-medium">
          {COMM_TEMPLATE.DRAG_TO_CHANGE_MESSAGE}
        </span>
        {attachments?.length === 0 ? (
          <span className="flex items-center justify-center h-full font-bold">
            {COMM_TEMPLATE.NO_ATTACHED_DOCUMENTS}
          </span>
        ) : (
          <DragDropContext onDragEnd={(result) => handleDragEnd(result)}>
            <Droppable droppableId="attachmentsList" direction="vertical">
              {(provided) => (
                <ul
                  className="attachments-list flex flex-col gap-4 mt-3"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ listStyle: 'none', padding: 0 }}
                >
                  {attachments?.map((item: Attachment, index: number) =>
                    item?.attachment_document_type !==
                    COMM_TEMPLATE.COVER_SHEET_KEY ? (
                      <DraggableSection
                        key={item.id}
                        item={item}
                        handleAttachmentDeleteClick={
                          handleAttachmentDeleteClick
                        }
                        handleAttachmentEditClick={handleAttachmentEditClick}
                        handleChange={handleChange}
                        index={index}
                        tabIndexOffset={tabIndexOffset}
                        isViewMode={isViewMode}
                      />
                    ) : null
                  )}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  )
}

export default DraggableAttachableList
